import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryState, parseAsString } from 'nuqs';
import { FolderPlus, Upload, Users } from 'lucide-react';
import { Button, Modal } from '@ntconsult/aluma-react';
import { EmptyState } from '@common/components/EmptyState';
import { useAuth } from '@features/auth';
import {
  CreateFolderModal,
  FolderHeaderActions,
  useBreadcrumb,
  useFolder,
  useFolderActions,
  useFolders,
  useSharedFolders,
} from '@features/folders';
import { useEffectiveAccess } from '@features/shares';
import { useFiles } from '../hooks/useFiles';
import { useSharedFiles } from '../hooks/useSharedFiles';
import { useProcessingEvents } from '../hooks/useProcessingEvents';
import { useFileActions } from '../hooks/useFileActions';
import { ExplorerListSkeleton, UnifiedExplorerList } from './UnifiedExplorerList';
import { ExplorerBreadcrumb } from './ExplorerBreadcrumb';
import { ExplorerDropzone } from './ExplorerDropzone';
import { FileUpload } from './FileUpload';
import type { File } from '../types';
import type { Folder } from '@features/folders';

export function FileExplorer() {
  const { t } = useTranslation(['files', 'folders', 'common']);
  const { folderId } = useParams();
  const [searchParam] = useQueryState('search', parseAsString);
  const search = searchParam ?? undefined;

  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin';

  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [droppedFiles, setDroppedFiles] = useState<globalThis.File[]>([]);

  const handleDroppedFiles = (accepted: globalThis.File[]) => {
    setDroppedFiles(accepted);
    setShowUpload(true);
  };

  const closeUploadModal = () => {
    setShowUpload(false);
    setDroppedFiles([]);
  };

  const { data: foldersData, isLoading: loadingFolders } = useFolders({
    parentId: folderId || null,
  });
  const { data: filesData, isLoading: loadingFiles } = useFiles({
    folderId,
    search,
    rootOnly: !folderId && !search,
  });
  const { data: breadcrumb } = useBreadcrumb(folderId);

  const { data: parentEffective } = useEffectiveAccess('folder', folderId, !!folderId);
  const { data: currentFolder } = useFolder(folderId);

  const isRootView = !folderId && !search;
  const canWriteHere = !folderId
    ? true
    : isAdmin || parentEffective?.permission === 'write';
  const canEditFolder =
    !!currentFolder && (isAdmin || currentFolder.createdBy === currentUser?.id);
  const canShareFolder = canEditFolder;

  const { handlers: folderHandlers, dialogs: folderDialogs } =
    useFolderActions(currentFolder);

  const { data: sharedFoldersData } = useSharedFolders();
  const { data: sharedFilesData } = useSharedFiles();

  const folders = foldersData?.data || [];
  const files = filesData?.data || [];
  const sharedFolders = isRootView ? sharedFoldersData?.data || [] : [];
  const sharedFiles = isRootView ? sharedFilesData?.data || [] : [];
  const hasShared = sharedFolders.length > 0 || sharedFiles.length > 0;
  const isLoading = loadingFolders || loadingFiles;

  useProcessingEvents(files);

  const folderCanWrite = (folder: Folder) =>
    isAdmin || folder.createdBy === currentUser?.id;
  const fileCanWrite = (file: File) =>
    isAdmin || file.uploadedBy === currentUser?.id;

  const { handlers, dialogs } = useFileActions();

  const writeDisabledTitle = !canWriteHere
    ? t('common:permissions.noWrite', { defaultValue: 'Você não tem permissão de escrita nesta pasta' })
    : undefined;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="min-w-0">
          <ExplorerBreadcrumb items={breadcrumb} />
          <h1 className="text-xl sm:text-2xl font-bold text-foreground truncate">
            {search
              ? `${t('files:searchResults')} "${search}"`
              : breadcrumb?.length
                ? breadcrumb[breadcrumb.length - 1].name
                : t('files:myFiles')}
          </h1>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {folderId && currentFolder && (
            <>
              <FolderHeaderActions
                canEdit={canEditFolder}
                canShare={canShareFolder}
                onShare={folderHandlers.share}
                onCopyLink={folderHandlers.copyLink}
                onDownload={folderHandlers.download}
                onRename={folderHandlers.rename}
                onDelete={folderHandlers.requestDelete}
              />
              <div className="hidden sm:block h-6 w-px bg-border" aria-hidden="true" />
            </>
          )}

          <Button
            variant="secondary"
            onClick={() => setShowCreateFolder(true)}
            disabled={!canWriteHere}
            title={writeDisabledTitle}
            aria-label={t('folders:newFolder')}
          >
            <FolderPlus className="w-4 h-4" />
            <span className="hidden sm:inline">{t('folders:newFolder')}</span>
          </Button>

          <Button
            onClick={() => setShowUpload(true)}
            disabled={!canWriteHere}
            title={writeDisabledTitle}
            aria-label={t('files:upload')}
          >
            <Upload className="w-4 h-4" />
            <span className="hidden sm:inline">{t('files:upload')}</span>
          </Button>
        </div>
      </div>

      <ExplorerDropzone
        folderName={breadcrumb?.length ? breadcrumb[breadcrumb.length - 1].name : undefined}
        disabled={!canWriteHere || !!search}
        onDrop={handleDroppedFiles}
      >
      {isLoading ? (
        <ExplorerListSkeleton />
      ) : folders.length === 0 && files.length === 0 && !hasShared ? (
        <EmptyState
          title={t('files:noFiles')}
          description={search ? t('files:noFilesSearch') : t('files:noFilesEmpty')}
          action={
            <div className="flex gap-2">
              <Button
                variant="secondary"
                onClick={() => setShowCreateFolder(true)}
                disabled={!canWriteHere}
              >
                <FolderPlus className="w-4 h-4" />
                {t('folders:newFolder')}
              </Button>
              <Button onClick={() => setShowUpload(true)} disabled={!canWriteHere}>
                <Upload className="w-4 h-4" />
                {t('files:uploadTitle')}
              </Button>
            </div>
          }
        />
      ) : (
        <div className="space-y-6">
          {(folders.length > 0 || files.length > 0) && (
            <UnifiedExplorerList
              scope={folderId ?? (search ? `search:${search}` : 'root')}
              folders={folders}
              files={files}
              folderCanWrite={folderCanWrite}
              fileCanWrite={fileCanWrite}
              onOpenFile={handlers.handlePlayFile}
              onDownloadFile={handlers.handleDownloadFile}
              onDeleteFile={handlers.handleDeleteFile}
              onShareFile={handlers.handleShareFile}
              onShareFolder={handlers.handleShareFolder}
              onDeleteFolder={handlers.handleDeleteFolder}
              onDownloadFolder={handlers.handleDownloadFolder}
              onBulkDownload={handlers.handleBulkDownload}
              onBulkDelete={handlers.handleBulkDelete}
            />
          )}

          {isRootView && hasShared && (
            <div className="space-y-3 border-t border-border pt-6">
              <h2 className="text-sm font-medium text-foreground-muted flex items-center gap-2">
                <Users className="w-4 h-4" />
                {t('common:sharedWithMe')}
              </h2>
              <UnifiedExplorerList
                scope="shared"
                folders={sharedFolders}
                files={sharedFiles}
                folderCanWrite={folderCanWrite}
                fileCanWrite={fileCanWrite}
                onOpenFile={handlers.handlePlayFile}
                onDownloadFile={handlers.handleDownloadFile}
                onDeleteFile={handlers.handleDeleteFile}
                onShareFile={handlers.handleShareFile}
                onShareFolder={handlers.handleShareFolder}
                onDeleteFolder={handlers.handleDeleteFolder}
                onDownloadFolder={handlers.handleDownloadFolder}
              />
            </div>
          )}
        </div>
      )}
      </ExplorerDropzone>

      <CreateFolderModal
        isOpen={showCreateFolder}
        onClose={() => setShowCreateFolder(false)}
        parentId={folderId}
      />

      <Modal
        open={showUpload}
        onClose={closeUploadModal}
        ariaLabel={t('files:upload')}
        size="lg"
      >
        <Modal.Body>
          {showUpload && (
            <FileUpload
              folderId={folderId || ''}
              initialFiles={droppedFiles.length > 0 ? droppedFiles : undefined}
              onClose={closeUploadModal}
            />
          )}
        </Modal.Body>
      </Modal>

      {dialogs}
      {folderDialogs}
    </div>
  );
}
